import {
  AlertColor,
  Box,
  Fab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  debounce,
} from '@mui/material';
import TitleText from '../../components/formlib/TitleText';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import { useCallback, useContext, useEffect, useState } from 'react';
import MediumTypography from '../../components/formlib/MediumTypography';
import SearchBox from '../../components/formlib/SearchBox';
import AppPagination from '../../components/shared/AppPagination';
import CaseloadEmployeesDropdown, {
  ServiceCoordinator,
} from './CaseloadEmployeesDropdown';
import CaseloadFilter from './CaseloadFilter';
import {
  CaseLoadClient,
  CaseloadListResponse,
  getCaseLoadList,
} from '../../services/configApi/codelist/caseloadlistService';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { Order } from '../referral/ReferralDashboard';
import TooltipTableCell from '../../components/formlib/TooltipTableCell';
import { useNavigate } from 'react-router-dom';
import { CLIENT_NAME_KEY, REFERRAL_ID_KEY } from '../../services/Constant';
import { FACESHEET } from '../../routes/Routing';
import ProductivityChart from '../activitySheet/ProductivityChart';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import {
  getAllReferrals,
  ReferralResponseType,
} from '../../services/configApi/Clients';

interface TableData {
  clientName: string;
  dateOfBirth: string;
  age: string;
  referralBeginDate: string;
  rollOverDate: string;
  ifspDate: string;
  reEvaluationDate: string;
  transitionPlanning: string;
  transitionPlanningConference: string;
  intakeFacilitatorName: string;
  serviceCoordinatorName: string;
  ifspFirstMeetingDate: string;
  ifspSignedDate: string;
}

const tableHeadCells: HeadCell<TableData>[] = [
  {
    id: 'clientName',
    labelId: 'Dashboard.ClientName',
    defaultLabelId: 'Client Name',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'dateOfBirth',
    labelId: 'Clientpage.dateofbirth',
    defaultLabelId: 'Date of Birth',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'age',
    labelId: 'ageText',
    defaultLabelId: 'Age',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'referralBeginDate',
    labelId: 'Facesheet.referralDate',
    defaultLabelId: 'Referral Begin Date',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'ifspDate',
    labelId: 'Facesheet.ifspDueDate',
    defaultLabelId: 'IFSP Due Date',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'reEvaluationDate',
    labelId: 'reEvalDateText',
    defaultLabelId: 'Re-eval Date',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'ifspFirstMeetingDate',
    labelId: 'ifspMeetingDateText',
    defaultLabelId: 'IFSP Meeting Date',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'ifspSignedDate',
    labelId: 'ifspSignedDateText',
    defaultLabelId: 'IFSP Signed Date',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'transitionPlanning',
    labelId: 'transitionPlanningText',
    defaultLabelId: 'Transition Planning',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'transitionPlanningConference',
    labelId: 'tpcText',
    defaultLabelId: 'TPC',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'intakeFacilitatorName',
    labelId: 'Facesheet.intakeFacilitator',
    defaultLabelId: 'Intake Facilitator',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'serviceCoordinatorName',
    labelId: 'Dashboard.serviceCoordinator',
    defaultLabelId: 'Service Coordinator',
    numeric: false,
    requiredSorting: true,
  },
];

const CaseLoadList = () => {
  const [caseload, setCaseload] = useState<CaseloadListResponse>();
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [selectedCoordinators, setSelectedCoordinators] = useState<
    ServiceCoordinator[]
  >([]);
  const navigate = useNavigate();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState<string>();
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [staffType, setStaffType] = useState<'all' | 'myTeam' | 'others'>(
    'others',
  );
  const [employeeIds, setEmployeeIds] = useState<string | null>(
    localStorage.getItem('userId'),
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [tileType, setTileType] = useState<
    'all' | 'clientsAbove27' | 'transitionCompleted'
  >('all');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof TableData>('ifspDate');
  const [refreshCoordinatorList, setRefreshCoordinatorList] = useState<
    string | null
  >(null);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof TableData,
  ) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    property = property === 'age' ? 'dateOfBirth' : property;
    setOrderBy(property);
  };

  useEffect(() => {
    const getResult = async () => {
      toggleLoader(true);
      await getCaseLoadList(
        staffType,
        employeeIds ?? '',
        searchQuery,
        tileType,
        pageNumber,
        orderBy,
        order,
      )
        .then((res) => {
          toggleLoader(false);
          res.productivity = [];
          setCaseload(res);
        })
        .catch((error) => {
          toggleLoader(false);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('failedCaseloadListMessage');
            setToastrDefaultMessage('Failed to load caseload list');
          }
        });
    };
    getResult();
  }, [
    staffType,
    employeeIds,
    searchQuery,
    tileType,
    pageNumber,
    order,
    orderBy,
  ]);

  const handleSearchChange = useCallback(
    debounce((value: string) => {
      setPageNumber(0);
      setSearchQuery(value);
    }, 500),
    [],
  );

  const getAllReferralsBasedOnSelectedClient = async (
    clientId: string,
    defaultReferralId: string,
  ) => {
    toggleLoader(true);
    if (clientId) {
      await getAllReferrals(clientId, defaultReferralId)
        .then(async (response: ReferralResponseType[]) => {
          if (response) {
            localStorage.setItem(
              'clientReferralDetails',
              JSON.stringify(response),
            );
          }
          navigate(FACESHEET);

          toggleLoader(false);
        })
        .catch((error) => {
          navigate(FACESHEET);

          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrVariable('error');
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrVariable('error');
            setToastrId('failedClientReferralDetailsMessage');
            setToastrDefaultMessage('Failed to load client referral details');
          }
        });
    }
  };

  const handleNavigate = async (clientDetails: CaseLoadClient) => {
    if (clientDetails.clientName !== null) {
      localStorage.setItem('ClientId', clientDetails.clientId);
      localStorage.setItem(CLIENT_NAME_KEY, clientDetails.clientName);
      localStorage.setItem(REFERRAL_ID_KEY, clientDetails.referralId);
      if (checkPermissionForFeature('backend.clients', 'viewPermission')) {
        getAllReferralsBasedOnSelectedClient(
          clientDetails.clientId,
          clientDetails.referralId ?? '',
        );
      }
    }
  };

  return (
    <Box component="main">
      {toastrId !== undefined && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
      )}
      <Toolbar disableGutters sx={{ display: 'flex' }}>
        <TitleText
          labelid="caseloadListText"
          defaultlabel="Caseload List"
          Sxprops={{ flex: 1 }}
        />
        <CaseloadEmployeesDropdown
          filterUpdate={refreshCoordinatorList}
          onItemSelect={(item) => {
            if (item.type === 'all' || item.type === 'myTeam') {
              setEmployeeIds('');
            }
            setTileType('all');
            setPageNumber(0);
            setStaffType(item.type);
            setSelectedCoordinators([item]);
          }}
          onItemsSelect={(items) => {
            setTileType('all');
            setStaffType('others');
            setPageNumber(0);
            setSelectedCoordinators(items);
            setEmployeeIds(items.map((item) => item.id).join(','));
            setRefreshCoordinatorList(null);
          }}
        />
        <Box sx={{ textAlign: 'right' }}>
          <SearchBox
            labelId="Clientdashboard.searchText"
            defaultlabel="Search Client"
            onChange={(value) => handleSearchChange(value)}
            sxProps={{
              backgroundColor: '#fff',
            }}
          />
        </Box>
      </Toolbar>

      {selectedCoordinators.length > 0 && (
        <Box component={'section'} className="mb-md">
          <CaseloadFilter
            items={selectedCoordinators}
            clearAll={() => {
              setStaffType('others');
              setSelectedCoordinators([]);
              setEmployeeIds(localStorage.getItem('userId'));
              setRefreshCoordinatorList('');
            }}
            onItemDelete={(item) => {
              if (item.type === 'all' || item.type === 'myTeam') {
                setPageNumber(0);
                setStaffType('others');
                setTileType('all');
                setSelectedCoordinators([]);
                setEmployeeIds(localStorage.getItem('userId'));
                setRefreshCoordinatorList('');
              } else {
                const coordinaters = selectedCoordinators.filter(
                  (i) => i.id !== item.id,
                );
                setSelectedCoordinators(coordinaters);
                if (coordinaters.length === 0) {
                  setRefreshCoordinatorList('');
                  setStaffType('others');
                  setTileType('all');
                  setEmployeeIds(localStorage.getItem('userId'));
                } else {
                  setRefreshCoordinatorList(item.id);
                  setEmployeeIds(coordinaters.map((i) => i.id).join(','));
                }
              }
            }}
          />
        </Box>
      )}

      {caseload && (
        <Box component={'section'} display={'flex'} width={'100%'}>
          <Grid
            container
            display={'flex'}
            sx={{ height: '100%', paddingTop: 1 }}
            columns={8}
          >
            <Fab
              variant="extended"
              size="small"
              className="fabStyles"
              sx={{
                backgroundColor: tileType === 'all' ? '#00C6B8' : '#ECFFFE',
                borderLeft: '4px solid #00C6B8',
                color: tileType === 'all' ? '#ECFFFE' : '#2a4241',
                '&:hover': {
                  backgroundColor: '#00C6B8',
                  color: '#ECFFFE',
                },
              }}
              onClick={() => {
                if (caseload.clientsCount > 0) {
                  setPageNumber(0);
                  setTileType('all');
                }
              }}
            >
              <MediumTypography
                labelid="noOfClientsAssignedText"
                defaultlabel="No. of clients assigned"
              />
              <Box className="pl-md">
                <MediumTypography
                  label={caseload.clientsCount.toString()}
                  fontweight={900}
                />
              </Box>
            </Fab>

            <Fab
              variant="extended"
              size="small"
              className="fabStyles"
              sx={{
                marginRight: '8px',
                marginLeft: '8px',
                backgroundColor:
                  tileType === 'clientsAbove27' ? '#00C6B8' : '#ECFFFE',
                borderLeft: '4px solid #00C6B8',
                color: tileType === 'clientsAbove27' ? '#ECFFFE' : '#2a4241',
                '&:hover': {
                  backgroundColor: '#00C6B8',
                  color: '#ECFFFE',
                },
              }}
              onClick={() => {
                if (caseload.clientsAbove27 > 0) {
                  setPageNumber(0);
                  setTileType('clientsAbove27');
                }
              }}
            >
              <MediumTypography
                labelid="clientsAgeMoreThan27Months"
                defaultlabel="Client's age more than 27 months"
              />
              <Box className="pl-md">
                <MediumTypography
                  label={caseload.clientsAbove27.toString()}
                  fontweight={900}
                />
              </Box>
            </Fab>

            <Fab
              variant="extended"
              size="small"
              className="fabStyles"
              sx={{
                backgroundColor:
                  tileType === 'transitionCompleted' ? '#00C6B8' : '#ECFFFE',
                borderLeft: '4px solid #00C6B8',
                color:
                  tileType === 'transitionCompleted' ? '#ECFFFE' : '#2a4241',
                '&:hover': {
                  backgroundColor: '#00C6B8',
                  color: '#ECFFFE',
                },
              }}
              onClick={() => {
                if (caseload.transitionCompleted > 0) {
                  setPageNumber(0);
                  setTileType('transitionCompleted');
                }
              }}
            >
              <MediumTypography
                labelid="tpcCompletedText"
                defaultlabel="Transition Planning Completed"
              />
              <Box className="pl-md">
                <MediumTypography
                  label={caseload.transitionCompleted.toString()}
                  fontweight={900}
                />
              </Box>
            </Fab>
          </Grid>

          <Box
            sx={{
              width: '35%',
              opacity: '0.5',
              pointerEvents: 'none',
              display: 'none',
            }}
          >
            <ProductivityChart
              productivity={caseload.productivity}
              horizontalValue={50}
              barHeight="115px"
            />
          </Box>
        </Box>
      )}

      {caseload && (
        <Grid
          container
          display={'flex'}
          justifyContent={'space-around'}
          className={'mt-md mb-md'}
        >
          <Grid item xs={12}>
            <TableContainer className="tableStyles">
              <Table
                sx={{
                  width: '100%',
                  maxWidth: '600px',
                  display: 'inline-block',
                }}
              >
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  className="listDataTableHead"
                  headerNames={tableHeadCells}
                  checkBoxRequired={false}
                  onRequestSort={handleRequestSort}
                />
                {caseload && caseload.clients !== null && (
                  <TableBody className="tableRowcss">
                    {caseload.clients.map((client, index) => (
                      <TableRow
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? '#ECF9F8' : '#ffffff',
                        }}
                        key={client.clientId}
                      >
                        <TooltipTableCell
                          textSxProps={{
                            color: '#008C82',
                            cursor: checkPermissionForFeature(
                              'backend.clients',
                              'viewPermission',
                            )
                              ? 'pointer'
                              : 'default',
                            maxWidth: '200px',
                          }}
                          value={
                            client.clientName !== null &&
                            client.clientName.length > 0
                              ? client.clientName
                              : '-'
                          }
                          visible={true}
                          onClick={() => {
                            handleNavigate(client);
                          }}
                        />
                        <TableCell>
                          <MediumTypography label={client.dateOfBirth ?? '-'} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <MediumTypography
                            label={
                              client.age !== null && client.age.length > 0
                                ? client.age
                                : '-'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <MediumTypography
                            label={
                              client.referralBeginDate !== null &&
                              client.referralBeginDate.length > 0
                                ? client.referralBeginDate
                                : '-'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <MediumTypography
                            label={
                              client.ifspDate !== null &&
                              client.ifspDate.length > 0
                                ? client.ifspDate
                                : '-'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <MediumTypography
                            label={
                              client.reEvaluationDate !== null &&
                              client.reEvaluationDate.length > 0
                                ? client.reEvaluationDate
                                : '-'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <MediumTypography
                            label={client.ifspFirstMeetingDate ?? '-'}
                          />
                        </TableCell>
                        <TableCell>
                          <MediumTypography
                            label={client.ifspSignedDate ?? '-'}
                          />
                        </TableCell>
                        <TableCell>
                          <MediumTypography
                            label={
                              client.transitionPlanning !== null &&
                              client.transitionPlanning.length > 0
                                ? client.transitionPlanning
                                : '-'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <MediumTypography
                            label={
                              client.transitionPlanningConference !== null &&
                              client.transitionPlanningConference.length > 0
                                ? client.transitionPlanningConference
                                : '-'
                            }
                          />
                        </TableCell>
                        <TooltipTableCell
                          textSxProps={{
                            maxWidth: '200px',
                          }}
                          value={client.intakeFacilitatorName ?? '-'}
                          visible={true}
                        />
                        <TooltipTableCell
                          textSxProps={{
                            maxWidth: '200px',
                          }}
                          value={client.serviceCoordinatorName ?? '-'}
                          visible={true}
                        />
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
              {caseload &&
                caseload.clients !== null &&
                caseload.clients.length === 0 && (
                  <Box component="div" className="width__100">
                    <MediumTypography
                      textstyle={'center'}
                      labelid="noClientsFoundMessage"
                      defaultlabel="No Clients found"
                      className="p-sm"
                    />
                  </Box>
                )}
            </TableContainer>
          </Grid>
        </Grid>
      )}
      {caseload && caseload.count > 10 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <AppPagination
            pageNumber={pageNumber}
            paginationCount={caseload.count}
            handleChangePage={(_event, newPage) => {
              if (newPage - 1 !== pageNumber) {
                setPageNumber(newPage - 1);
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CaseLoadList;
